"use client";

import { getTranslatedHrtName } from "@/app/directories/shop/helper";
import { getCopyForHrtOptionsGrid } from "@frontend/components/priceToggle/priceToggle";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import type { PricingMapEntries } from "@frontend/lib/stores/slices/pricingMapSlice";
import { cn } from "@frontend/shared/lib/utils";
import { useLocale } from "next-intl";
import type { HrtOption } from "./ProductContext";
import { renderPrice } from "./VerticalHrtOptionsGrid";

export interface HrtOptionsGridProps {
  options: HrtOption[];
  selectedHrtId: number | undefined;
  onHrtSelect: (hrtId: number | undefined) => void;
  pricingMap: PricingMapEntries;
  calculatePrice: (option: HrtOption, pricingMap: PricingMapEntries) => number;
  isInitialRequired?: boolean;
  noInsuranceProcedures?: boolean;
  secondaryColor?: string;
}

export function HrtOptionsGrid({
  options,
  selectedHrtId,
  onHrtSelect,
  pricingMap,
  calculatePrice,
  isInitialRequired,
  noInsuranceProcedures,
  secondaryColor,
}: HrtOptionsGridProps) {
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();

  if (!options || options.length === 0) {
    return null;
  }

  const t = useTranslations("Shop");
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  return (
    <div
      className={cn(
        "grid w-full",
        options.length > 2 && "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
        options.length === 2 && "grid-cols-5 gap-4",
        options.length === 1 && "grid-cols-1",
        options.length >= 3 && "gap-3",
      )}
    >
      {options.map((option) => {
        const priceEntry = pricingMap[option.hrt.hrtId];
        const price = calculatePrice(option, pricingMap);

        if (Number.isNaN(price) || price === undefined) {
          const isInsuredMode = priceEntry?.mode === "INSURED";
          const isLoading = priceEntry === undefined;
          const isInsuredPending = isPendingPricesMode && isInsuredMode;

          return (
            <div
              key={option.hrt.hrtId}
              className={cn(
                "flex flex-col lg:gap-1 sm:gap-0 border rounded-[20px] lg:px-5 sm:px-3 py-3 text-left transition-colors mx-2",
                selectedHrtId === option.hrt.hrtId
                  ? [
                      "border-primary bg-primary/5",
                      options.length === 2 && "col-span-3",
                    ]
                  : [
                      "hover:border-primary/50",
                      options.length === 2 && "col-span-2",
                    ],
              )}
            >
              <p className="text-sm font-bold">
                {getTranslatedHrtName(option.hrt, languageCode)}
              </p>
              {renderPrice(0, isLoading, isInsuredPending, t)}
            </div>
          );
        }

        const isInsured = priceEntry.mode === "INSURED";
        const isLoading = priceEntry === undefined;
        const isInsuredPending = isInsured && isPendingPricesMode;

        return (
          <button
            key={option.hrt.hrtId}
            type="button"
            onClick={() => onHrtSelect(option.hrt.hrtId)}
            className={cn(
              "flex flex-col lg:gap-1 sm:gap-0 border rounded-[20px] lg:px-5 sm:px-3 py-3 text-left transition-colors lg:min-h-[90px]",
              selectedHrtId === option.hrt.hrtId
                ? [
                    "border-primary bg-primary/5",
                    options.length === 2 && "col-span-3",
                  ]
                : [
                    "hover:border-primary/50",
                    options.length === 2 && "col-span-2",
                  ],
            )}
          >
            <p className="lg:font-bold sm:font-medium sm:text-sm lg:text-sm lg:leading-[18px] sm:leading-[18px]">
              {getTranslatedHrtName(option.hrt, languageCode)}
            </p>
            <div className="flex flex-col items-start lg:gap-2 sm:gap-0">
              <div className="flex items-center gap-2">
                {renderPrice(price, isLoading, isInsuredPending, t)}
              </div>
              {getCopyForHrtOptionsGrid(
                isInsured,
                noInsuranceProcedures || false,
                isPendingPricesMode,
                t,
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
